import { PanelLeftClose, PanelLeftOpen, Pencil } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation } from 'react-router';

import LogoMark from '@/assets/brand/logo-mark.svg?react';
import { brand } from '@/config/brand';
import { DisplayNameDialog } from '@/features/account/components/DisplayNameDialog';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

import { useWorkspaceHomeLink, useWorkspaceNav } from './useWorkspaceNav';

interface WorkspaceSidebarProps {
  /** 모바일 시트 안에서 렌더될 때 항목 클릭 시 닫기. */
  onNavigate?: () => void;
  /** 접힌 아이콘 레일 모드 — 로고·아이콘만 남기고 텍스트 블록은 숨긴다(라벨은 sr-only + title). */
  rail?: boolean;
  /** 접힘 토글(데스크톱) — 지정하면 메뉴 목록 아래에 라벨형 "메뉴 접기/펼치기" 항목을 그린다. 모바일 시트는 미지정. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
}

/**
 * 좌측 내비게이션(Left Nav) — Admin Kit sidebar 규격(sidebar 면 · 항목 h-9 text-sm · 선택 항목 sidebar-accent).
 * DESIGN.md: 내비 전체를 primary 로 칠하지 않는다. 메뉴 항목은 config/workspace-nav.ts SSoT.
 * 폭은 부모 컨테이너가 정한다(w-full) — 레이아웃이 접힘/펼침 전환을 width 트랜지션으로 처리한다.
 */
export function WorkspaceSidebar({
  onNavigate,
  rail = false,
  collapsed = false,
  onToggleCollapse,
  className,
}: WorkspaceSidebarProps) {
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const toggleLabel = collapsed ? '메뉴 펼치기' : '메뉴 접기';
  const { pathname } = useLocation();
  const navItems = useWorkspaceNav();
  const homeLink = useWorkspaceHomeLink();
  const name = useAuthStore((s) => s.name);
  const email = useAuthStore((s) => s.email);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);

  return (
    <aside
      className={cn(
        'flex h-full w-full flex-col overflow-hidden border-r border-sidebar-border bg-sidebar whitespace-nowrap text-sidebar-foreground',
        className,
      )}
      data-rail={rail || undefined}
    >
      <NavLink
        to={homeLink}
        className={cn(
          'flex flex-col gap-1 pt-5',
          rail ? 'items-center px-3 pb-6' : 'px-5 pb-5',
          // 모바일 시트 안 — 시트 닫기 버튼(우상단) 자리만큼 비운다
          onNavigate && 'pr-12',
        )}
        onClick={onNavigate}
        title={rail ? brand.name : undefined}
      >
        <span className="flex items-center gap-3">
          <LogoMark className="h-8 w-auto flex-none" aria-hidden="true" />
          {!rail && (
            <strong className="text-lg font-semibold tracking-tight">
              {brand.wordmark.primary}
              {brand.wordmark.accent}
            </strong>
          )}
        </span>
        {!rail && (
          <span className="text-xs font-medium text-muted-foreground">경영지도사 워크스페이스</span>
        )}
      </NavLink>

      {!rail && (
        // 사용자 카드 — 누르면 표시 이름 변경(specs/053 FR-006)
        <button
          type="button"
          className="group mx-3 mb-5 flex items-center gap-3 rounded-md border bg-card px-3 py-2.5 text-left transition-colors hover:bg-sidebar-accent"
          title="표시 이름 변경"
          onClick={() => setNameDialogOpen(true)}
        >
          <span className="size-2 flex-none rounded-full bg-success" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <strong className="block truncate text-sm font-medium">{name ?? '워크스페이스'}</strong>
            <span className="block truncate text-xs text-muted-foreground">{email ?? ''}</span>
          </span>
          <Pencil
            className="size-3.5 flex-none text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            aria-hidden="true"
          />
        </button>
      )}
      <DisplayNameDialog open={nameDialogOpen} onOpenChange={setNameDialogOpen} />

      {!rail && <p className="mx-5 mb-1 text-xs font-medium text-muted-foreground">업무 메뉴</p>}
      <nav className="px-3" aria-label="워크스페이스 메뉴">
        {navItems.map((item) => {
          const active = pathname === item.to || pathname.startsWith(`${item.match ?? item.to}/`);
          return (
            <NavLink
              key={item.key}
              to={item.to}
              onClick={onNavigate}
              title={rail ? item.label : undefined}
              className={cn(
                'my-0.5 flex h-9 w-full items-center gap-3 rounded-md text-left text-sm transition-colors',
                rail ? 'justify-center px-0' : 'px-3',
                active
                  ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground'
                  : 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              )}
            >
              <item.icon
                className={cn('size-4 flex-none', !active && 'text-muted-foreground')}
                aria-hidden="true"
              />
              <span className={cn('flex-1', rail && 'sr-only')}>{item.label}</span>
            </NavLink>
          );
        })}
        {onToggleCollapse && (
          <div className="mx-2 mt-2 border-t border-sidebar-border" aria-hidden="true" />
        )}
        {onToggleCollapse && (
          <button
            type="button"
            title={rail ? toggleLabel : undefined}
            aria-expanded={!collapsed}
            className={cn(
              'mt-1 flex h-9 w-full items-center gap-3 rounded-md text-left text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              rail ? 'justify-center px-0' : 'px-3',
            )}
            onClick={onToggleCollapse}
          >
            <ToggleIcon className="size-4 flex-none" aria-hidden="true" />
            <span className={cn('flex-1', rail && 'sr-only')}>{toggleLabel}</span>
          </button>
        )}
      </nav>

      {!rail && (
        <div className="mt-auto px-5 pt-6 pb-5">
          <div className="flex items-center justify-between border-t border-sidebar-border pt-3 text-xs font-medium">
            {brand.group}
            <span className="font-normal text-muted-foreground">React v0.1</span>
          </div>
        </div>
      )}
    </aside>
  );
}
